/**
 * Shared PII-stripping helper used by every adapter as defense-in-depth
 * before forwarding event properties to a third-party SDK.
 *
 * Call sites remain responsible for not passing PII in the first place,
 * but a flat blocklist of well-known field names catches the common
 * mistakes — accidentally splatting a user object into `attributes`,
 * passing `email` "just for debugging", etc.
 *
 * Originally GA-only because GA4's terms of service prohibit PII in the
 * event stream and Google enforces it strictly. Now applied across every
 * destination for two reasons:
 *
 *   1. GDPR resilience — none of our destinations need PII in event
 *      properties; sending it accidentally is a compliance footgun.
 *   2. Symmetry — making the strip provider-agnostic means a reviewer
 *      checking "where does PII land?" only has one helper to audit.
 *
 * The `identify()` path is intentionally NOT stripped — that's where
 * intentional, governed user traits flow. Per-destination allowlists
 * (see GA adapter's `GA_USER_PROPERTY_ALLOWLIST`) gate identify traits.
 */

/**
 * Well-known PII field names. Stored already normalized
 * (`normalizePiiKey`) so `EMAIL`, `email_address`, and `emailAddress`
 * all hit the same entry.
 */
const PII_KEYS_BLOCKLIST = new Set([
  'email',
  'emailaddress',
  'name',
  'firstname',
  'lastname',
  'fullname',
  'phone',
  'phonenumber',
  'domain',
  'address',
]);

/**
 * Lowercase + strip non-alphanumeric so callers can't smuggle PII past
 * the blocklist with trivial casing/punctuation variants.
 */
function normalizePiiKey(key: string): string {
  return key.toLowerCase().replaceAll(/[^a-z0-9]/g, '');
}

/**
 * Returns a shallow copy of `props` with well-known PII keys removed.
 * Preserves `undefined` when the caller passed no properties so the
 * wire format to the underlying SDK matches the pre-strip shape — e.g.
 * `posthog.capture(event)` vs `posthog.capture(event, {})` may be
 * indistinguishable in practice but the latter is a subtle behavioural
 * change we don't want to bake in.
 *
 * The non-undefined return is always an object, so adapters that spread
 * the result (`{ ...stripPiiKeys(x), ...rest }`) work without a null
 * guard.
 */
export function stripPiiKeys(
  props: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (props === undefined) return undefined;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (PII_KEYS_BLOCKLIST.has(normalizePiiKey(key))) continue;
    result[key] = value;
  }
  return result;
}
